import React, { useEffect, useRef, useState } from "react";
import ScrollDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import DOMPurify from "dompurify";

import aiLogo from "../../../asset/images/aiLogo.png";
import pauseIcon from "../../../asset/images/pauseIcon.png";
import selectIcon from "../../../asset/images/selectIcon.png";
import tradeSenceLogoWM from "../../../asset/images/tradeSenceLogoWM.png";
import { getReferentielDataFromServer } from "../../../common/components/general/AxiosReferencielInterceptor";
import { useTSData } from "../../../common/components/general/TSDataContext";
import { HOME_PAGE_ROUTE } from "../../../common/constants/routerConstants";
import MessageAIComponent from "./MessageAIComponent";
import config from "../../../config";

const ChatMessaging = ({
  isHistoriqueBarOpen,
  sessionActive,
  setSessionActive,
  isMobile,
  mode = "init",
  setMode,
  addToHistory,
  ...props
}) => {
  const { messageActuel, setMessageActuel } = useTSData();

  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [messageId, setMessageId] = useState(null);
  const [inputMessage, setInputMessage] = useState("");
  const [aiIsGenerating, setAiIsGenerating] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const chatRef = useRef(null);
  const [lineTyped, setLineTyped] = useState(0);
  const abortControllerRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [speechSupported] = useState(true);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Erreur microphone:", error);
      alert("Impossible d'accéder au microphone");
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setAiIsGenerating(true);

    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      const response = await fetch(config.aiChatBotUrl + "/speech-to-text", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Erreur transcription");
      }

      const data = await response.json();

      if (data.text) {
        setInputMessage((prev) => (prev + " " + data.text).trim());
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la transcription");
    } finally {
      setAiIsGenerating(false);
    }
  };

  const globalStyles = `
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    
    @keyframes ripple {
      0% { 
        transform: translate(-50%, -50%) scale(0.8);
        opacity: 1;
      }
      100% { 
        transform: translate(-50%, -50%) scale(2);
        opacity: 0;
      }
    }
  `;

  // Injecter les styles
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = globalStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const fetchConversation = async () => {
    const response = await getReferentielDataFromServer(
      true,
      config.aiChatBotUrl + `/messages2/${sessionActive}`,
      {},
      "get"
    );
    if (response.error) {
      let url = new URL(window.location.href);
      url.searchParams.delete("sessionId");
      window.history.pushState({}, "", url);
      setSessionActive(null);
      return;
    }
    setMessages(
      response.map((m) => ({
        id: m.id,
        sender: m.role === "human" ? "user" : "ai",
        text: m.content,
        is_good_answered: m.is_good_answered,
      }))
    );
  };

  useEffect(() => {
    if (mode === "init") {
      if (!!messageActuel) {
        handleSendMessage(messageActuel);
        setMessageActuel(null);
      } else if (!!sessionActive) fetchConversation();
      else {
        setMessages([]);
      }
    }
  }, [messageActuel, sessionActive]);

  useEffect(() => {
    const fetchMessage = async () => {
      if (!!messageId) {
        let _messages = [...messages];
        let message = _messages.find(
          (m) => m.id === messageId && m.sender === "ai"
        );
        let userMessage = _messages.find(
          (m) => m.id === messageId && m.sender === "user"
        );

        if (message) {
          let _sessionId = sessionActive;
          if (!_sessionId) {
            _sessionId = uuidv4();
            setSessionActive(_sessionId);
            addToHistory({
              id: _sessionId,
              title:
                userMessage.text.slice(0, 50) +
                `${userMessage.text.length > 50 ? "..." : ""}`,
              date: new Date(),
            });
            let url = new URL(window.location.href);
            url.searchParams.set("sessionId", _sessionId);
            window.history.pushState({}, "", url);
          }

          abortControllerRef.current = new AbortController();
          const signal = abortControllerRef.current.signal;

          try {
            const response = await fetch(config.aiChatBotUrl + "/ask", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                session_id: _sessionId,
                query: userMessage.text,
              }),
              signal: signal,
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let partialData = "";

            setAiIsGenerating(true);

            const updatedMessages = _messages.map((m) =>
              m.id === messageId && m.sender === "ai"
                ? { ...m, text: "", tokens: [] }
                : m
            );

            setMessages(updatedMessages);
            let lastMessageForSessionId = null;
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                lastMessageForSessionId = await getReferentielDataFromServer(
                  false,
                  "api/messages/last-id/" + _sessionId,
                  {},
                  "get"
                );
                setMessageId(lastMessageForSessionId);
                break;
              }

              partialData += decoder.decode(value, { stream: true });

              const lines = partialData.split("[]");
              for (let i = 0; i < lines.length - 1; i++) {
                const line = lines[i];

                let token;
                if (line.startsWith("data: ")) {
                  token = line.replace("data: ", "");
                } else if (!token) {
                  token = "\n";
                }

                setMessages((prevMessages) =>
                  prevMessages.map((m) =>
                    m.id === messageId && m.sender === "ai"
                      ? {
                          ...m,
                          text: m.text + token,
                          tokens: m.tokens
                            ? [...m.tokens, token].join("")
                            : token,
                        }
                      : m
                  )
                );

                if (chatRef.current) {
                  chatRef.current.scrollTop = chatRef.current.scrollHeight;
                }
              }

              partialData = lines[lines.length - 1];
            }

            setMessages((prevMessages) =>
              prevMessages.map((m) =>
                m.id === messageId && m.sender === "ai"
                  ? {
                      ...m,
                      loading: false,
                      id: lastMessageForSessionId || messageId,
                    }
                  : m
              )
            );
            setAiIsGenerating(false);
          } catch (error) {
            if (error.name === "AbortError") {
              console.log("Fetch aborted");
            } else {
              console.error("Error in streaming:", error);
            }
            setAiIsGenerating(false);
          }
        }
      }
    };

    fetchMessage();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [messageId]);

  const handleScroll = () => {
    const scrollHeight = chatRef.current.scrollHeight;
    const clientHeight = chatRef.current.clientHeight;
    if (chatRef.current.scrollTop < scrollHeight - clientHeight) {
      setShowScrollDown(true);
    }
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      // Only auto-scroll when AI is generating a response
      if (chatRef.current && aiIsGenerating) {
        chatRef.current.scrollTo({
          top: chatRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    });

    if (chatRef.current) {
      observer.observe(chatRef.current, {
        subtree: true,
        attributeOldValue: true,
        characterData: true,
        attributes: true,
        childList: true,
      });
    }

    return () => observer.disconnect();
  }, [aiIsGenerating]);

  useEffect(() => {
    const chatElement = chatRef.current;
    if (chatElement) {
      chatElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatElement) {
        chatElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const blockActuelMessage = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setAiIsGenerating(false);
    }
  };

  const handleSendMessage = (message) => {
    if (message && !aiIsGenerating) {
      setMode("messaging");
      let messageId = message.id || uuidv4();
      setAiIsGenerating(true);
      setMessages([
        ...messages,
        { id: messageId, text: message, sender: "user" },
        { id: messageId, text: "", sender: "ai", loading: true },
      ]);
      setMessageId(messageId);
    }
  };

  const getLineCount = (message) => {
    return message?.split("\n")?.length;
  };

  const handleSendInputMessage = () => {
    if (aiIsGenerating) {
      blockActuelMessage();
    } else if (inputMessage.trim() !== "" && !aiIsGenerating) {
      const lineCount = getLineCount(inputMessage);
      if (lineCount + lineTyped > 10) {
        setShowScrollDown(true);
      } else {
        setLineTyped(lineCount + lineTyped + 1);
      }
      handleSendMessage(inputMessage);
      setInputMessage("");
    }
  };

  const handleMessageFeedback = async (
    messageId,
    feedbackType,
    reasons = null,
    feedbackText = null
  ) => {
    try {
      const targetMessage = messages.find(
        (m) => m.id === messageId && m.sender === "ai"
      );
      if (!targetMessage) {
        console.error("Message not found for feedback");
        return;
      }

      const feedbackData = {
        id: messageId,
        reasons: reasons?.map((reason) => reason.id).join(", "),
        isGoodAnswered: feedbackType === "good",
        comment:
          feedbackText ||
          (Array.isArray(reasons) ? reasons.join(", ") : reasons) ||
          "",
      };

      const response = await getReferentielDataFromServer(
        false,
        `api/messages/${messageId}/feedback`,
        feedbackData,
        "post"
      );

      if (response.error) {
        console.error("Error submitting feedback:", response.error);
      } else {
        console.log("Feedback submitted successfully:", feedbackData);
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  return (
    <Box
      sx={{
        height: isMobile ? "70vh" : "60vh",
        position: "relative",
        display: "flex",
        marginTop: isMobile ? "0px" : "-25px",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Paper
        elevation={0}
        ref={chatRef}
        sx={{
          flex: 1,
          padding: !!isHistoriqueBarOpen ? "1px" : "20px",
          width: !!isHistoriqueBarOpen ? "80%" : "60%",
          marginLeft: !!isHistoriqueBarOpen ? "14%" : "19%",
          ...(isMobile && {
            marginLeft: "1px",
            width: "100%",
            padding: "1px",
            marginTop: "50px",
          }),
          overflow: "auto",
          maxHeight: "100%",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent:
                message.sender === "user" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            {message.sender !== "user" && (
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "40px",
                    height: "40px",
                    marginLeft: "10px",
                    marginTop: "20px",
                    border: "1px solid red",
                    borderRadius: "50%",
                  }}
                >
                  <img
                    src={aiLogo}
                    alt="AI Logo"
                    width="90%"
                    height="90%"
                    style={{
                      paddingLeft: "10px",
                      paddingTop: "5px",
                      paddingBottom: "5px",
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box
              sx={{
                maxWidth: "100%",
                padding: "10px",
                margin: "10px",
                borderRadius: "13px",
                backgroundColor:
                  message.sender === "user" ? "#F5E2E3" : "#FFFFFF",
                color: message.sender === "user" ? "#0B0D0A" : "#000",
              }}
            >
              {message.sender === "user" ? (
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "16.53px",
                    fontWeight: 500,
                    lineHeight: "24.8px",
                    textAlign: "left",
                    textUnderlinePosition: "from-font",
                    textDecorationSkipInk: "none",
                    color: "#0B0D0A",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(message.text.replace(/\n/g, "<br />")),
                  }}
                />
              ) : (
                <MessageAIComponent
                  message={message?.text}
                  messageId={message?.id}
                  onFeedback={handleMessageFeedback}
                  showFeedback={!message?.loading}
                  is_good_answered={message?.is_good_answered}
                />
              )}
            </Box>
          </Box>
        ))}
        {/* <Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
            }}
          >
            {messages
              .filter((m) => m.sender === "ai" && m.tokens)
              .flatMap((m) => m.tokens || [])
              .map((token, index) => (
                <MessageAIComponent message={token} />
              ))}
          </Box>
        </Box> */}
      </Paper>

      <Box
        sx={{
          position: "fixed",
          width: "60%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignContent: "center",
          mt: 2,
          marginBottom: "20px",
          left: !!isHistoriqueBarOpen ? "35%" : "20%",
          bottom: "30px",
        }}
      >
        {showScrollDown && (
          <Box
            sx={{
              position: "absolute",
              top: -40,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            <IconButton
              onClick={() => {
                if (chatRef.current) {
                  chatRef.current.scrollTop = chatRef.current.scrollHeight;
                }
              }}
            >
              <ScrollDownIcon sx={{ fontSize: 30, color: "#7E7E7E" }} />
            </IconButton>
          </Box>
        )}
        <TextField
          fullWidth
          name="message"
          multiline
          variant="outlined"
          placeholder="Message TIJARIA..."
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.shiftKey) {
              setInputMessage((prevInput) => prevInput + "\n");
              e.preventDefault();
            } else if (e.key === "Enter") {
              handleSendInputMessage();
              e.preventDefault();
            }
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              borderColor: "inherit",
              borderRadius: "10px",
              maxHeight: isMobile ? "100px" : "200px",
              overflowY: "auto",
            },
            endAdornment: (
              <InputAdornment position="end">
                {speechSupported && (
                  <IconButton
                    onClick={isRecording ? stopRecording : startRecording}
                    sx={{
                      color: isRecording ? "#f44336" : "#666",
                      backgroundColor: isRecording
                        ? "rgba(244, 67, 54, 0.1)"
                        : "transparent",
                      marginRight: "8px",
                      position: "relative",
                      animation: isRecording ? "pulse 1.5s infinite" : "none",
                      "&::before": isRecording
                        ? {
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(244, 67, 54, 0.3)",
                            borderRadius: "50%",
                            transform: "translate(-50%, -50%)",
                            animation: "ripple 1.5s infinite",
                            zIndex: -1,
                          }
                        : {},
                    }}
                  >
                    {isRecording ? (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M6 6h12v12H6z" />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                      </svg>
                    )}
                  </IconButton>
                )}
                <IconButton onClick={handleSendInputMessage} edge="end">
                  <img
                    src={aiIsGenerating ? pauseIcon : selectIcon}
                    alt="Send Icon"
                    style={{
                      width: "28px",
                      height: "28px",
                      marginRight: "1px",
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Alert message */}
        <Box
          sx={{
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "6px",
            borderLeft: "4px solid #2196f3",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              color: "#666666",
              fontFamily: "Montserrat",
              lineHeight: 1.4,
            }}
          >
            TijarIA s’améliore continuellement. Ses réponses sont fournies à
            titre informatif et ne remplacent pas les textes réglementaires, les
            procédures officielles ni l’avis des services compétents.
          </Typography>
        </Box>
      </Box>
      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            display: "flex",
            ...(isMobile && { flexDirection: "column" }),
            top: isMobile || isHistoriqueBarOpen ? "30px" : "40px",
            left: "10px",
            gap: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={tradeSenceLogoWM}
            alt="Logo"
            sx={{
              width: "222px",
              height: "42px",
              marginBottom: "-10px",
              marginLeft: "5px",
              cursor: "pointer",
            }}
            onClick={() => {
              navigate(HOME_PAGE_ROUTE);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ChatMessaging;
